import React, {useContext, useState} from 'react';
import moment from "moment";
import __ from "lodash";
import {Grid, Icon, IconButton, Tooltip, Typography} from "@material-ui/core";

import SystemContext from "../../../SystemContext";
import {ComboSelect, DatePicker, FlightNumber, FormDialog, MaskedTime} from "../../../controls";
import AirportLookup from "../../Airports/components/AirportsLookup";
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";
import Validator from "../validators/AircraftMovementCaptureValidator";

import {useLastAircraftMovement, useAddAircraftMovement, useAddAircraft} from "../../../services/aircraft";


const allTypes = ["PUSH-BACK", "TAKE-OFF", "LANDING", "ON-BLOCKS", "SUMMARY", "PUSH-BACK"];
const takeoff = ["TAKE-OFF", "LANDING", "ON-BLOCKS", "SUMMARY"];
const landing = ["LANDING", "ON-BLOCKS", "SUMMARY"];
const blocksOn = ["ON-BLOCKS", "SUMMARY"];

const parseFlightNumber = (flightNumber) => {
  const flightParts = flightNumber.match(/[0-9]?[A-Z]{1,2}([0-9]{3,4}[A-Z]?)/) || [""];
  const flightNo = flightParts.length > 1 ? flightParts[1] : flightParts[0];
  const airlineCode = flightNumber.replace(flightNo, "");

  return {flightNo, airlineCode}
}


const AircraftMovementCaptureDialog = (props) => {

  const classes = props.classes;
  const SystemData = useContext(SystemContext);

  const now = moment.utc();
  const minDate = moment.utc().subtract(6, 'month');
  const maxDate = moment(minDate).add(3, 'year');
  const initialDate = now.toISOString();
  const dateFormat = "DD/MM/YYYY";

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: {etd: initialDate},
    handleSave: null
  });

  const _airlineCode = props.airlineCode;
  const _flightNo = props.flightNo;
  const originAirportCode = props.originAirportCode;
  const destinationAirportCode = props.destinationAirportCode;
  const tripLegUuid = props.tripLegUuid;

  const [movementRecord, setMovementRecord] = useState(null);

  const handleAddMovement = useAddAircraftMovement();

  const extraKeys = tripLegUuid ? [props.aircraftTailNumber, tripLegUuid] : [props.aircraftTailNumber];
  const lastMovement = useLastAircraftMovement({extraKeys});
  if (lastMovement && __.has(lastMovement, "status") && lastMovement.status === "success" && !movementRecord) {
    let data = {
      movementType: "SUMMARY",
      flightNumber: ""
    }
    if (lastMovement.data) {
      data = lastMovement.data;
    }
    const movementType = data.movementType === 'RETURN-TO-STAND'?'SUMMARY':data.movementType;
    const {flightNo, airlineCode} = parseFlightNumber(data.flightNumber);
    const nextMovementType = movementType === "SUMMARY" && tripLegUuid ? "SUMMARY" : allTypes[allTypes.indexOf(movementType) + 1];
    const newMovement = {
      lastMovementType: data.movementType,
      lastAirlineCode: airlineCode,
      lastFlightNo: flightNo,
      lastFlightDate: data.flightDateUTC,
      lastOrigin: data.originAirportCodeIata,
      lastDestination: data.destinationAirportCodeIata,
      aircraftTailNumber: props.aircraftTailNumber,
      movementType: nextMovementType,
      flightNumber: tripLegUuid ? `${_airlineCode}${_flightNo}` : (movementType !== "SUMMARY" ? data.flightNumber : `${_airlineCode}${_flightNo}`||""),
      airlineCode: tripLegUuid ? _airlineCode : movementType !== "SUMMARY" ? airlineCode : _airlineCode||"",
      flightNo: tripLegUuid ? _flightNo : movementType !== "SUMMARY" ? flightNo : _flightNo||"",
      flightDateUTC: movementType !== "SUMMARY" ? data.flightDateUTC : "",
      originAirportCodeIata: movementType !== "SUMMARY" ? data.originAirportCodeIata : originAirportCode||"",
      originAirportCodeIcao: movementType !== "SUMMARY" ? data.originAirportCodeIcao : "",
      destinationAirportCodeIata: movementType !== "SUMMARY" ? data.destinationAirportCodeIata : destinationAirportCode||"",
      destinationAirportCodeIcao: movementType !== "SUMMARY" ? data.destinationAirportCodeIcao : "",
      offBlocksTimeUTC: data.offBlocksTimeUTC ? data.offBlocksTimeUTC : initialDate,
      takeOffTimeUTC: data.takeOffTimeUTC ? data.takeOffTimeUTC : null,
      landingTimeUTC: data.landingTimeUTC ? data.landingTimeUTC : null,
      onBlocksTimeUTC: data.onBlocksTimeUTC ? data.onBlocksTimeUTC : null,
      etaUTC: data.etaUtc ? data.etaUtc : null,
      upliftFuelKg: 0,
      movementFuelKg:	0,
      emailUuid: null,
      processedUTC: null,
      processedStatus: null,
      lastMovement: lastMovement.data,
      tripLegUuid: tripLegUuid
    }

    setMovementRecord(newMovement);
  }

  return (
    <div key="formContainer" style={{width: "100%", textAlign:"left" }}>
    {movementRecord
      ?
        <FormDialog
          title={props.title}
          message={lastMovement.data?`Last recorded movement: ${__.capitalize(movementRecord.lastMovementType).replace(/-/g,' ')} of flight ${movementRecord.lastAirlineCode}-${movementRecord.lastFlightNo} (${movementRecord.lastOrigin}-${movementRecord.lastDestination}) on ${moment(movementRecord.lastFlightDate).format(dateFormat)}`:''}
          open={props.open}
          record={movementRecord}
          dialogOptions={{maxWidth: "sm", fullWidth: false}}
          movable={true}
          classes={classes}
          validator={Validator}
          buttons = {{
            save:{icon: "check_circle", key: "Update", label: "Update", color: "primary", disabled: (status)=>{return status.error}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }}
          onChange={(state) => {
            if (state.buttonPressed === "save") {
              SystemData.toggleLoadingProgress(true);

              const movement = state.record;
              let offBlocks = null;
              let takeOff = null;
              let eta  = null;
              let landing = null;
              let onBlocks = null;

              switch (movement.movementType) {
                case "PUSH-BACK":
                  offBlocks = movement.offBlocksTimeUTC ? movement.offBlocksTimeUTC : initialDate;
                  break;
                case "TAKE-OFF":
                  offBlocks = movement.offBlocksTimeUTC ? movement.offBlocksTimeUTC : initialDate;
                  takeOff = movement.takeOffTimeUTC ? movement.takeOffTimeUTC : offBlocks;
                  eta = movement.etaUTC ? movement.etaUTC : takeOff;
                  break;
                case "LANDING":
                  offBlocks = movement.offBlocksTimeUTC ? movement.offBlocksTimeUTC : initialDate;
                  takeOff = movement.takeOffTimeUTC ? movement.takeOffTimeUTC : offBlocks;
                  eta = movement.etaUTC ? movement.etaUTC : takeOff;
                  landing = movement.landingTimeUTC ? movement.landingTimeUTC : eta;
                  break;
                case "ON-BLOCKS":
                  offBlocks = movement.offBlocksTimeUTC ? movement.offBlocksTimeUTC : initialDate;
                  takeOff = movement.takeOffTimeUTC ? movement.takeOffTimeUTC : offBlocks;
                  eta = movement.etaUTC ? movement.etaUTC : takeOff;
                  landing = movement.landingTimeUTC ? movement.landingTimeUTC : eta;
                  onBlocks = movement.onBlocksTimeUTC ? movement.onBlocksTimeUTC : landing;
                  break;
                case "SUMMARY":
                  if (tripLegUuid) {
                    offBlocks = movement.offBlocksTimeUTC ? movement.offBlocksTimeUTC : initialDate;
                    takeOff = movement.takeOffTimeUTC ? movement.takeOffTimeUTC : offBlocks;
                    eta = movement.etaUTC ? movement.etaUTC : takeOff;
                    landing = movement.landingTimeUTC ? movement.landingTimeUTC : eta;
                    onBlocks = movement.onBlocksTimeUTC ? movement.onBlocksTimeUTC : landing;
                  }
                  break;
                default:
              }

              const record = {
                ...movement,
                movementType: movement.movementType,
                offBlocksTimeUTC: offBlocks,
                takeOffTimeUTC: takeOff,
                landingTimeUTC: landing,
                onBlocksTimeUTC: onBlocks,
                etaUTC: eta,
              }

              handleAddMovement.mutate({
                extraKeys: [],
                record: record,
                postSuccess: (result) => {
                  SystemData.toggleLoadingProgress(false);
                  props.onChange();
                },
                postError: (data) => {
                  SystemData.toggleLoadingProgress(false);
                  if (data.errorMessage) {
                    if (__.has(data.errorMessage, "response") &&
                        __.has(data.errorMessage.response, "data") &&
                        __.has(data.errorMessage.response.data, "errorMessage") &&
                        data.errorMessage.response.data.errorMessage !== "") {
                      const actualError = data.errorMessage.response.data.errorMessage;
                      SystemData.alert("Error", actualError);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred saving the Movement. ${data.errorMessage}`);
                    }
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred saving the Movement`);
                  }
                }
              });
            } else {
              setMovementRecord(null);
              props.onChange();
            }
          }}
        >
          <div style={{marginBottom: 20}}></div>

          <Grid container checkChildren={true} spacing={2}>

            <Grid item xs={2} checkChildren={true} row>
              <div style={{position: "relative", fontWeight: 1000, top: 10}}>Movement:</div>
            </Grid>
            <Grid item xs={4} checkchildren="true">
              <ComboSelect
                id="movementType"
                group="MOVEMENTS"
                subGroup=""
                style={{marginTop: "3px"}}
                fullWidth
                onChange={(value, props) => {
                  let offBlocks = null;
                  let takeOff = null;
                  let eta  = null;
                  let landing = null;
                  let onBlocks = null;

                  switch (value) {
                    case "PUSH-BACK":
                      offBlocks = props.record.offBlocksTimeUTC ? props.record.offBlocksTimeUTC : initialDate;
                      break;
                    case "TAKE-OFF":
                      offBlocks = props.record.offBlocksTimeUTC ? props.record.offBlocksTimeUTC : initialDate;
                      takeOff = props.record.takeOffTimeUTC ? props.record.takeOffTimeUTC : offBlocks;
                      eta = props.record.etaUTC ? props.record.etaUTC : takeOff;
                      break;
                    case "LANDING":
                      offBlocks = props.record.offBlocksTimeUTC ? props.record.offBlocksTimeUTC : initialDate;
                      takeOff = props.record.takeOffTimeUTC ? props.record.takeOffTimeUTC : offBlocks;
                      eta = props.record.etaUTC ? props.record.etaUTC : takeOff;
                      landing = props.record.landingTimeUTC ? props.record.landingTimeUTC : eta;
                      break;
                    case "ON-BLOCKS":
                      offBlocks = props.record.offBlocksTimeUTC ? props.record.offBlocksTimeUTC : initialDate;
                      takeOff = props.record.takeOffTimeUTC ? props.record.takeOffTimeUTC : offBlocks;
                      eta = props.record.etaUTC ? props.record.etaUTC : takeOff;
                      landing = props.record.landingTimeUTC ? props.record.landingTimeUTC : eta;
                      onBlocks = props.record.onBlocksTimeUTC ? props.record.onBlocksTimeUTC : landing;
                      break;
                    case "SUMMARY":
                      if (tripLegUuid) {
                        offBlocks = props.record.offBlocksTimeUTC ? props.record.offBlocksTimeUTC : initialDate;
                        takeOff = props.record.takeOffTimeUTC ? props.record.takeOffTimeUTC : offBlocks;
                        eta = props.record.etaUTC ? props.record.etaUTC : takeOff;
                        landing = props.record.landingTimeUTC ? props.record.landingTimeUTC : eta;
                        onBlocks = props.record.onBlocksTimeUTC ? props.record.onBlocksTimeUTC : landing;
                      }
                      break;
                    default:
                  }

                  const data = {
                    ...props.record,
                    movementType: value,
                    offBlocksTimeUTC: offBlocks,
                    takeOffTimeUTC: takeOff,
                    landingTimeUTC: landing,
                    onBlocksTimeUTC: onBlocks,
                    etaUTC: eta,
                  }
                  props.recordChange(data);
                }}
              />
            </Grid>
            <Grid item xs={6} checkChildren={true} row>
              {/*Placeholder*/}
            </Grid>
            {tripLegUuid?<></>:
            <Grid item xs={2} checkChildren={true} row>
              <div style={{position: "relative", fontWeight: 1000, top: 10}}>Flight:</div>
            </Grid>}
            {tripLegUuid?<></>:
            <Grid item xs={4} checkchildren="true">
              {record=>
                <FlightNumber
                  id="flightNumber"
                  // label="Flight"
                  mask={"aX-999aa"}
                  code={record.airlineCode}
                  number={record.flightNo}
                  //inputProps={defaultEditProps}
                  onChange={(e, props) => {
                    const data = {
                      ...props.record,
                      airlineCode: e.code,
                      flightNo: e.number,
                      flightNumber: e.code + e.number
                    }
                    props.recordChange(data);
                  }}
                />
              }
            </Grid>}
            {tripLegUuid?<></>:
            <Grid item xs={6} checkChildren={true} row>
              {/*Placeholder*/}
            </Grid>}
            {tripLegUuid?<></>:
            <Grid item xs={2} checkChildren={true} row>
              <div style={{position: "relative", fontWeight: 1000, top: 10}}>Origin:</div>
            </Grid>}
            {tripLegUuid?<></>:
            <Grid item xs={4} checkChildren={true} row>
              <AirportLookup
                id="originAirportCodeIata"
                //label="Origin Airport"
                inputLabelProps={{ shrink: true }}
                style={{width: "100%"}}
                returnType={"object"}
                onChange={(returnObject, props) => {
                  if (returnObject && returnObject.found) {
                    const data = {
                      ...props.record,
                      originAirportCodeIata: returnObject.value.toUpperCase(),
                      originAirportName: returnObject.data.airportName,
                      originAirportCodeIcao: returnObject.data.icaoCode
                    }
                    props.recordChange(data);
                  } else {
                    props.onChange(returnObject.value);
                  }
                }}
              />
              {(record) => (
                <AirportNameTooltip
                  airportCode={record.originAirportCodeIata}
                  onChange={(airport, props)=> {
                    if (record.originAirportCodeIata !== "" && !__.isEmpty(airport) && airport.airportName !== props.record.originAirportName) {
                      const data = {
                        ...props.record,
                        originAirportName: airport.airportName,
                      }
                      props.recordChange(data);
                    }
                  }}
                />
              )}
            </Grid>}
            {tripLegUuid?<></>:
            <Grid item xs={6} checkchildren="true">
              {record => (
                <div style={{display: "inline-block", width: 300, maxWidth: 400, whiteSpace:"nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: -15}}>
                  <Typography style={{ fontFamily: 'inherit', whiteSpace: 'nowrap', variant: "subtitle1", marginTop: 20, overflow: "hidden", textOverflow: "ellipsis"}}>
                    {record.originAirportName}
                  </Typography>
                </div>
              )}
            </Grid>}
            {tripLegUuid?<></>:
            <Grid item xs={2} checkChildren={true} row>
              <div style={{position: "relative", fontWeight: 1000, top: 10}}>Destination:</div>
            </Grid>}
            {tripLegUuid?<></>:
            <Grid item xs={4} checkChildren={true} row>
              <AirportLookup
                id="destinationAirportCodeIata"
                inputLabelProps={{ shrink: true }}
                style={{width: "100%"}}
                returnType={"object"}
                onChange={(returnObject, props) => {
                  if (returnObject && returnObject.found) {
                    const data = {
                      ...props.record,
                      destinationAirportCodeIata: returnObject.value.toUpperCase(),
                      destinationAirportName: returnObject.data.airportName,
                      destinationAirportCodeIcao: returnObject.data.icaoCode
                    }
                    props.recordChange(data);
                  } else {
                    props.onChange(returnObject.value);
                  }
                }}
              />
              {(record) => (
                <AirportNameTooltip
                  airportCode={record.destinationAirportCodeIata}
                  onChange={(airport, props)=> {
                    if (record.destinationAirportCodeIata !== "" && !__.isEmpty(airport) && airport.airportName !== props.record.destinationAirportName) {
                      const data = {
                        ...props.record,
                        destinationAirportName: airport.airportName,
                      }
                      props.recordChange(data);
                    }
                  }}
                />
              )}
            </Grid>}
            {tripLegUuid?<></>:
            <Grid item xs={6} checkchildren="true">
              {record => (
                <div style={{display: "inline-block", width: 300, maxWidth: 400, whiteSpace:"nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: -15}}>
                  <Typography style={{ fontFamily: 'inherit', whiteSpace: 'nowrap', variant: "subtitle1", marginTop: 20, overflow: "hidden", textOverflow: "ellipsis"}}>
                    {record.destinationAirportName}
                  </Typography>
                </div>
              )}
            </Grid>}

            <div style={{marginBottom: 50}}></div>

            {/*================*/}
            {/*    Pushback    */}
            {/*================*/}

            <Grid item xs={2} checkChildren={true} row>
              <div style={{position: "relative", fontWeight: 1000, top: 10}}>Pushback:</div>
            </Grid>
            <Grid item xs={4} checkChildren={true} row>
              <DatePicker
                id="offBlocksTimeUTC"
                style={{width: "100%"}}
                minDate={minDate}
                maxDate={maxDate}
                defaultValue={initialDate}
                disableToolbar={true}
                autoOk={true}
                format={dateFormat}
                onChange={(value, props) => {
                  const {record, recordChange} = props;
                  const previousValue = moment.utc(record.offBlocksTimeUTC);
                  const newValue = moment.utc(value).startOf('day')
                    .add(previousValue.hours(), 'hour')
                    .add(previousValue.minutes(), 'minute')
                    .toISOString();
                  recordChange({
                    offBlocksTimeUTC: newValue
                  });
                }}
              />
            </Grid>
            <Grid item xs={3} checkChildren={true} row>
              {(record, changeRecord) => (
                <MaskedTime
                  id="offBlocksTimeUTC"
                  source="timestamp"
                  onChange={(value)=>{
                    if (!value) return;
                    if (typeof value !== 'string' || value.indexOf(':') < 1 || value.split(':').length !== 2) return;
                    value = value.replace(/\s/g, '0');
                    const [hours,  minutes] =  value.split(':');
                    if (isNaN(hours) || hours < 0 || hours > 23) return;
                    if (isNaN(minutes) || minutes < 0 || hours > 59) return;
                    let pushback = record.offBlocksTimeUTC;
                    const newPushbackTime = moment.utc(pushback).startOf('day')
                      .add(hours, 'hour')
                      .add(minutes, 'minute')
                      .toISOString();
                    changeRecord({
                      offBlocksTimeUTC: newPushbackTime,
                    });
                  }}
                />
              )}
            </Grid>
            <Grid item xs={3} checkChildren={true} row>
              {(record, changeRecord) => (
                <Tooltip title="Revert to last movement's Pushback">
                  <IconButton
                    style={{padding:2}}
                    disabled={ ! (__.has(record, "lastMovement") && __.has(record.lastMovement, "offBlocksTimeUTC")) }
                    onClick={()=> {
                      changeRecord({
                        offBlocksTimeUTC: record.lastMovement.offBlocksTimeUTC
                      })
                  }}><Icon>restore</Icon></IconButton>
                </Tooltip>
              )}
            </Grid>

            {/*================*/}
            {/*Take-off and ETA*/}
            {/*================*/}

            {(record, changeRecord) => (
              takeoff.includes(record.movementType)
              ?
                <Grid container checkChildren={true} spacing={2} style={{padding: 6}}>
                  <Grid item xs={2} checkChildren={true} row>
                    <div style={{position: "relative", fontWeight: 1000, top: 10}}>Takeoff:</div>
                  </Grid>
                  <Grid item xs={4} checkChildren={true} row>
                    <DatePicker
                      id="takeOffTimeUTC"
                      style={{width: "100%"}}
                      minDate={record.offBlocksTimeUTC}
                      maxDate={maxDate}
                      defaultValue={initialDate}
                      disableToolbar={true}
                      autoOk={true}
                      format={dateFormat}
                      onChange={(value, props) => {
                        const {record, recordChange} = props;
                        const previousValue = moment.utc(record.takeOffTimeUTC);
                        const newValue = moment.utc(value).startOf('day')
                          .add(previousValue.hours(), 'hour')
                          .add(previousValue.minutes(), 'minute')
                          .toISOString();
                        recordChange({
                          takeOffTimeUTC: newValue
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} checkChildren={true} row>
                    <MaskedTime
                      id="takeOffTimeUTC"
                      source="timestamp"
                      onChange={(value)=>{
                        if (!value) return;
                        if (typeof value !== 'string' || value.indexOf(':') < 1 || value.split(':').length !== 2) return;
                        value = value.replace(/\s/g, '0');
                        const [hours,  minutes] =  value.split(':');
                        if (isNaN(hours) || hours < 0 || hours > 23) return;
                        if (isNaN(minutes) || minutes < 0 || hours > 59) return;
                        let takeOff = record.takeOffTimeUTC ?? record.offBlocksTimeUTC;
                        const newTakeoffTime = moment.utc(takeOff).startOf('day')
                          .add(hours, 'hour')
                          .add(minutes, 'minute')
                          .toISOString();
                        changeRecord({
                          takeOffTimeUTC: newTakeoffTime,
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} checkChildren={true} row>
                    <Tooltip title="Revert to last movement's Takeoff">
                      <IconButton
                        style={{padding:2}}
                        disabled={ ! (__.has(record, "lastMovement") && __.has(record.lastMovement, "takeOffTimeUTC")) }
                        onClick={()=> {
                          changeRecord({
                            takeOffTimeUTC: record.lastMovement.takeOffTimeUTC
                          })
                      }}><Icon>restore</Icon></IconButton>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={2} checkChildren={true} row>
                    <div style={{position: "relative", fontWeight: 1000, top: 10}}>ETA:</div>
                  </Grid>
                  <Grid item xs={4} checkChildren={true} row>
                    <DatePicker
                      id="etaUTC"
                      style={{width: "100%"}}
                      minDate={record.takeOffTimeUTC}
                      maxDate={maxDate}
                      defaultValue={initialDate}
                      disableToolbar={true}
                      autoOk={true}
                      format={dateFormat}
                      onChange={(value, props) => {
                        const {record, recordChange} = props;
                        const previousValue = moment.utc(record.etaUTC);
                        const newValue = moment.utc(value).startOf('day')
                          .add(previousValue.hours(), 'hour')
                          .add(previousValue.minutes(), 'minute')
                          .toISOString();
                        recordChange({
                          etaUTC: newValue
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} checkChildren={true} row>
                    <MaskedTime
                      id="etaUTC"
                      source="timestamp"
                      onChange={(value)=>{
                        if (!value) return;
                        if (typeof value !== 'string' || value.indexOf(':') < 1 || value.split(':').length !== 2) return;
                        value = value.replace(/\s/g, '0');
                        const [hours,  minutes] =  value.split(':');
                        if (isNaN(hours) || hours < 0 || hours > 23) return;
                        if (isNaN(minutes) || minutes < 0 || hours > 59) return;
                        let eta = record.etaUTC ?? record.takeOffTimeUTC;
                        const newEta = moment.utc(eta).startOf('day')
                          .add(hours, 'hour')
                          .add(minutes, 'minute')
                          .toISOString();
                        changeRecord({
                          etaUTC: newEta,
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} checkChildren={true} row>
                    <Tooltip title="Revert to last movement's ETA">
                      <IconButton
                        style={{padding:2}}
                        disabled={ ! (__.has(record, "lastMovement") && __.has(record.lastMovement, "etaUtc")) }
                        onClick={()=> {
                          changeRecord({
                            etaUTC: record.lastMovement.etaUtc
                          })
                      }}><Icon>restore</Icon></IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              :
                <></>
            )}

            {/*================*/}
            {/*    Landing     */}
            {/*================*/}

            {(record, changeRecord) => (
              landing.includes(record.movementType)
                ?
                  <Grid container checkChildren={true} spacing={2} style={{padding: 6}}>
                    <Grid item xs={2} checkChildren={true} row>
                      <div style={{position: "relative", fontWeight: 1000, top: 10}}>Landing:</div>
                    </Grid>
                    <Grid item xs={4} checkChildren={true} row>
                      {(record, changeRecord) => (
                        <DatePicker
                          id="landingTimeUTC"
                          style={{width: "100%"}}
                          minDate={record.takeOffTimeUTC}
                          maxDate={maxDate}
                          defaultValue={initialDate}
                          disableToolbar={true}
                          autoOk={true}
                          format={dateFormat}
                          onChange={(value, props) => {
                            const {record, recordChange} = props;
                            const previousValue = moment.utc(record.landingTimeUTC);
                            const newValue = moment.utc(value).startOf('day')
                              .add(previousValue.hours(), 'hour')
                              .add(previousValue.minutes(), 'minute')
                              .toISOString();
                            recordChange({
                              landingTimeUTC: newValue
                            });
                          }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={3} checkChildren={true} row>
                      <MaskedTime
                        id="landingTimeUTC"
                        source="timestamp"
                        onChange={(value)=>{
                          if (!value) return;
                          if (typeof value !== 'string' || value.indexOf(':') < 1 || value.split(':').length !== 2) return;
                          value = value.replace(/\s/g, '0');
                          const [hours,  minutes] =  value.split(':');
                          if (isNaN(hours) || hours < 0 || hours > 23) return;
                          if (isNaN(minutes) || minutes < 0 || hours > 59) return;
                          let landing = record.landingTimeUTC ?? record.etaUTC;
                          const newLandingTime = moment.utc(landing).startOf('day')
                            .add(hours, 'hour')
                            .add(minutes, 'minute')
                            .toISOString();
                          changeRecord({
                            landingTimeUTC: newLandingTime
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={3} checkChildren={true} row>
                      {(record, changeRecord) => (
                        <Tooltip title="Revert to last movement's Landing">
                          <IconButton
                            style={{padding:2}}
                            disabled={ ! (__.has(record, "lastMovement") && __.has(record.lastMovement, "landingTimeUTC")) }
                            onClick={()=> {
                              changeRecord({
                                landingTimeUTC: record.lastMovement.landingTimeUTC
                              })
                          }}><Icon>restore</Icon></IconButton>
                        </Tooltip>
                      )}
                    </Grid>

                  </Grid>
                :
                  <></>
            )}


            {/*================*/}
            {/*    blocks-on   */}
            {/*================*/}

            {(record, changeRecord) => (
              blocksOn.includes(record.movementType)
              ?
                <Grid container checkChildren={true} spacing={2} style={{padding: 6}}>
                  <Grid item xs={2} checkChildren={true} row>
                    <div style={{position: "relative", fontWeight: 1000, top: 10}}>Blocks-on:</div>
                  </Grid>
                  <Grid item xs={4} checkChildren={true} row>
                    {(record, changeRecord) => (
                      <DatePicker
                        id="onBlocksTimeUTC"
                        style={{width: "100%"}}
                        minDate={record.landingTimeUTC}
                        maxDate={maxDate}
                        defaultValue={initialDate}
                        disableToolbar={true}
                        autoOk={true}
                        format={dateFormat}
                        onChange={(value, props) => {
                          const {record, recordChange} = props;
                          const previousValue = moment.utc(record.onBlocksTimeUTC);
                          const newValue = moment.utc(value).startOf('day')
                            .add(previousValue.hours(), 'hour')
                            .add(previousValue.minutes(), 'minute')
                            .toISOString();
                          recordChange({
                            onBlocksTimeUTC: newValue
                          });
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={3} checkChildren={true} row>
                    {(record, changeRecord) => (
                      <MaskedTime
                        id="onBlocksTimeUTC"
                        source="timestamp"
                        onChange={(value)=>{
                          if (!value) return;
                          if (typeof value !== 'string' || value.indexOf(':') < 1 || value.split(':').length !== 2) return;
                          value = value.replace(/\s/g, '0');
                          const [hours,  minutes] =  value.split(':');
                          if (isNaN(hours) || hours < 0 || hours > 23) return;
                          if (isNaN(minutes) || minutes < 0 || hours > 59) return;
                          let blocksOn = record.onBlocksTimeUTC ?? record.landingTimeUTC;
                          const newBlocksTime = moment.utc(blocksOn).startOf('day')
                            .add(hours, 'hour')
                            .add(minutes, 'minute')
                            .toISOString();
                          changeRecord({
                            onBlocksTimeUTC: newBlocksTime
                          });
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={3} checkChildren={true} row>
                    {(record, changeRecord) => (
                      <Tooltip title="Revert to last movement's Blocks-on">
                        <IconButton
                          style={{padding:2}}
                          disabled={ ! (__.has(record, "lastMovement") && __.has(record.lastMovement, "onBlocksTimeUTC")) }
                          onClick={()=> {
                            changeRecord({
                              onBlocksTimeUTC: record.lastMovement.onBlocksTimeUTC
                            })
                        }}>
                          <Icon>restore</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>

                </Grid>
              :
                <></>
            )}

          </Grid>

        </FormDialog>
      :
        <></>
    }
    </div>
  )
}

export default AircraftMovementCaptureDialog;
